import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCheck, FileText, RotateCcw, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { billingApi } from '@/lib/api/endpoints';
import type { BillingState, LedgerItem } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT, type MessageKey } from '@/lib/i18n';
import { formatAmount, formatMinutes } from '@/routes/Clients';
import { cn } from '@/lib/utils';

/** The four states, in the order work moves through them. */
const STATES: BillingState[] = [
  'UNBILLED',
  'INVOICED',
  'PARTIAL',
  'PAID',
  'WRITTEN_OFF',
];

const TONE: Record<BillingState, string> = {
  UNBILLED: 'bg-accent/10 text-accent',
  INVOICED: 'bg-amber/10 text-amber',
  // Part paid is still partly owed, so it keeps the amber of a debt rather
  // than the green of money that has all arrived.
  PARTIAL: 'bg-amber/10 text-amber',
  PAID: 'bg-success-subtle text-success',
  WRITTEN_OFF: 'bg-surface-sunken text-content-subtle',
};

/**
 * Every billable hour and amount, and what you can do about it.
 *
 * This replaced a screen that could only show the current period's unbilled
 * time and issue all of it at once. Nothing else was possible: not invoicing
 * half of it, not recording that a client had paid part, not correcting a
 * figure that came out wrong — so the totals drifted away from the bank and
 * there was no way back.
 *
 * Hence a ledger rather than a summary. Every row is one item with its own
 * state, every row can be picked, and what you pick you can invoice, mark
 * invoiced, mark paid, write off, or put back. The buttons are the whole
 * point: they are how the screen is made to agree with reality.
 */
export function BillingLedger({
  clientId,
  projectId,
  currency,
  onIssue,
}: {
  clientId?: string;
  projectId?: string;
  /** Used only for the empty totals line, before any row has said one. */
  currency?: string;
  /**
   * Issue a protocol from the picked items. Only the client screen can: a
   * protocol is addressed to one client, and a project may span several.
   *
   * The period comes from the picked days rather than from two date fields
   * nobody wanted to fill in — what you ticked *is* the period.
   */
  onIssue?: (selection: {
    entry_ids: string[];
    task_ids: string[];
    period_start: string;
    period_end: string;
  }) => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  // Unbilled first: the reason you opened this screen is usually "what do I
  // still have to invoice".
  const [filter, setFilter] = useState<BillingState | 'ALL'>('UNBILLED');
  // Non-null while a payment is being entered. A payment has a size, and
  // assuming it is the whole selection is the assumption that made the old
  // screen disagree with the bank.
  const [payment, setPayment] = useState<string | null>(null);
  const isPhone = usePhone();
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data, isPending } = useQuery({
    queryKey: ['ledger', clientId ?? null, projectId ?? null, filter],
    queryFn: () =>
      billingApi.ledger({
        client_id: clientId,
        project_id: projectId,
        state: filter === 'ALL' ? undefined : [filter],
      }),
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const selected = useMemo(
    () => items.filter((item) => picked.has(key(item))),
    [items, picked],
  );

  const move = useMutation({
    mutationFn: ({ state, paid }: { state: BillingState; paid?: string }) =>
      billingApi.setLedgerState({
        ...selectionOf(selected),
        state,
        ...(paid ? { paid_amount: paid } : {}),
      }),
    onSuccess: (result, { state }) => {
      toast.show(t('ledger.moved', { count: result.updated, state: t(label(state)) }));
      setPicked(new Set());
      setPayment(null);
      invalidate(queryClient);
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const toggle = (item: LedgerItem) =>
    setPicked((current) => {
      const next = new Set(current);
      const id = key(item);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allPicked = items.length > 0 && selected.length === items.length;

  /** Record what was typed: the whole selection, or a payment of that size. */
  const record = () => {
    const typed = (payment ?? '').trim().replace(/\s/g, '').replace(',', '.');
    if (!/^\d+(\.\d{1,2})?$/.test(typed) || Number(typed) <= 0) {
      toast.error(t('ledger.paidNothing'));
      return;
    }
    // Paying the lot needs no arithmetic on the server, and asking it to
    // spread a payment that covers everything would only invite a rounding
    // argument with the total the person just read.
    const whole = Number(typed) >= Number(dueTotal(selected) || 0);
    move.mutate({ state: 'PAID', paid: whole ? undefined : typed });
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {(['ALL', ...STATES] as const).map((state) => (
          <button
            key={state}
            type="button"
            onClick={() => {
              setFilter(state);
              setPicked(new Set());
            }}
            aria-pressed={filter === state}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs transition-surface max-sm:min-h-9 touch:min-h-9',
              filter === state
                ? 'bg-accent/10 font-medium text-accent'
                : 'text-content-muted hover:bg-surface-hover hover:text-content',
            )}
          >
            {t(state === 'ALL' ? 'ledger.all' : label(state))}
            {totalOf(data?.totals?.[state as BillingState]) !== null ? (
              <span className="ml-1.5 tabular-nums opacity-70">
                {totalOf(data?.totals?.[state as BillingState])}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {isPending ? (
        <p className="px-1 py-3 text-xs text-content-subtle">…</p>
      ) : items.length === 0 ? (
        <p className="px-1 py-3 text-sm text-content-subtle">{t('ledger.empty')}</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-content-subtle">
                  <th className="w-9 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={allPicked}
                      aria-label={t('ledger.selectAll')}
                      onChange={() =>
                        setPicked(allPicked ? new Set() : new Set(items.map(key)))
                      }
                      className="h-4 w-4 accent-[oklch(52.6%_0.096_150)] max-sm:h-6 max-sm:w-6 touch:h-6 touch:w-6"
                    />
                  </th>
                  <th className="px-1 py-2 text-left font-medium max-sm:hidden">
                    {t('ledger.day')}
                  </th>
                  {/* w-full: the one column that should absorb the spare width, so the
                      titles read instead of truncating at a dozen characters. */}
                  <th className="w-full px-1 py-2 text-left font-medium">
                    {t('ledger.work')}
                  </th>
                  <th className="px-1 py-2 text-right font-medium">{t('ledger.amount')}</th>
                  <th className="px-2 py-2 text-left font-medium">{t('ledger.state')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={key(item)}
                    className={cn(
                      'border-b border-border last:border-0',
                      picked.has(key(item)) && 'bg-accent/5',
                    )}
                  >
                    <td className="px-2 py-2 align-top max-sm:py-3">
                      <input
                        type="checkbox"
                        checked={picked.has(key(item))}
                        onChange={() => toggle(item)}
                        aria-label={item.task_title}
                        className="h-4 w-4 accent-[oklch(52.6%_0.096_150)] max-sm:h-6 max-sm:w-6 touch:h-6 touch:w-6"
                      />
                    </td>
                    <td className="whitespace-nowrap px-1 py-2 align-top text-content-subtle tabular-nums max-sm:hidden">
                      {formatDay(item.day)}
                    </td>
                    <td className="max-w-0 px-1 py-2 align-top">
                      <span className="block truncate text-content">{item.task_title}</span>
                      <span className="block truncate text-[11px] text-content-subtle">
                        {[
                          // The date column is gone at this width, so the day
                          // rides here instead of disappearing.
                          isPhone ? formatDay(item.day) : null,
                          item.kind === 'FIXED'
                            ? t('ledger.fixed')
                            : formatMinutes(item.minutes),
                          // Only worth saying where a row could be from
                          // anywhere: inside one project it is every row.
                          projectId ? null : item.project_name,
                          item.note,
                          item.report_number,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-1 py-2 text-right align-top tabular-nums text-content">
                      {formatAmount(Number(item.amount), item.currency)}
                      {item.paid_amount ? (
                        <span className="block text-[11px] font-normal text-content-subtle">
                          {t('ledger.paidOf', {
                            paid: formatAmount(Number(item.paid_amount), item.currency),
                            total: formatAmount(Number(item.amount), item.currency),
                          })}
                        </span>
                      ) : null}
                      {item.due ? null : (
                        <span className="block text-[11px] font-normal text-content-subtle">
                          {t('ledger.notDue')}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <span
                        className={cn(
                          'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                          TONE[item.state],
                        )}
                        title={item.state_at ? formatDay(item.state_at.slice(0, 10)) : undefined}
                      >
                        {t(label(item.state))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* The bar that makes the screen worth having: what you picked, and
              every direction it can be moved in — including backwards. */}
          {selected.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-surface-sunken p-2">
              <span className="px-1 text-xs text-content-muted">
                {t('ledger.picked', {
                  count: selected.length,
                  amount: sumOf(selected, currency),
                })}
              </span>
              <span className="flex-1" />
              {onIssue ? (
                <Button size="sm" onClick={() => onIssue(periodOf(selected))}>
                  <FileText className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {t('ledger.issue')}
                </Button>
              ) : null}
              <Action
                icon={Send}
                label="ledger.markInvoiced"
                onClick={() => move.mutate({ state: 'INVOICED' })}
                busy={move.isPending}
              />
              <Action
                icon={CheckCheck}
                label="ledger.markPaid"
                onClick={() => {
                  const due = dueTotal(selected);
                  if (due === '' && currenciesOf(selected).size > 1) {
                    toast.error(t('ledger.oneCurrency'));
                    return;
                  }
                  setPayment(due);
                }}
                busy={move.isPending}
              />
              <Action
                icon={Ban}
                label="ledger.writeOff"
                onClick={() => move.mutate({ state: 'WRITTEN_OFF' })}
                busy={move.isPending}
              />
              <Action
                icon={RotateCcw}
                label="ledger.markUnbilled"
                onClick={() => {
                  // Putting an item back takes it off our protocol, so the
                  // next one can pick it up without billing it twice. That is
                  // a real consequence and it gets a real question.
                  if (
                    selected.every((item) => !item.report_number) ||
                    window.confirm(t('ledger.unbillConfirm'))
                  ) {
                    move.mutate({ state: 'UNBILLED' });
                  }
                }}
                busy={move.isPending}
              />
            </div>
          ) : null}

          {/* How much actually arrived. Pre-filled with what the picked items
              still owe, so a full payment is one more click; typing less
              records a part payment and leaves the rest owed. */}
          {payment !== null && selected.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-surface p-2 ring-1 ring-accent">
              <label className="flex items-center gap-2 text-xs text-content">
                {t('ledger.payment')}
                <input
                  value={payment}
                  inputMode="decimal"
                  autoFocus
                  onChange={(event) => setPayment(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') record();
                    if (event.key === 'Escape') setPayment(null);
                  }}
                  className="w-28 rounded-lg bg-surface px-2 py-1 text-sm tabular-nums text-content ring-1 ring-border focus:outline-none focus:ring-accent max-sm:min-h-10 touch:min-h-10"
                />
              </label>
              <span className="text-[11px] text-content-subtle">
                {t('ledger.paymentHint')}
              </span>
              <span className="flex-1" />
              <Button variant="ghost" size="sm" onClick={() => setPayment(null)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" disabled={move.isPending} onClick={record}>
                {t('ledger.paymentConfirm')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function Action({
  icon: Icon,
  label: message,
  onClick,
  busy,
}: {
  icon: typeof Send;
  label: MessageKey;
  onClick: () => void;
  busy: boolean;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-content transition-surface ring-1 ring-border hover:bg-surface-hover disabled:opacity-50 max-sm:min-h-10 touch:min-h-10"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {t(message)}
    </button>
  );
}

/** Whether the date column is on screen, which decides where the day is shown. */
function usePhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const listen = () => setPhone(query.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);
  return phone;
}

/** A time entry and a task can share an id space; the kind keeps them apart. */
function key(item: LedgerItem): string {
  return `${item.kind}:${item.id}`;
}

function selectionOf(items: LedgerItem[]): { entry_ids: string[]; task_ids: string[] } {
  return {
    entry_ids: items.filter((item) => item.kind === 'TIME').map((item) => item.id),
    task_ids: items.filter((item) => item.kind === 'FIXED').map((item) => item.id),
  };
}

/** The picked items, plus the span of days they cover. */
function periodOf(items: LedgerItem[]) {
  const days = items.map((item) => item.day).sort();
  return {
    ...selectionOf(items),
    period_start: days[0] ?? '',
    period_end: days[days.length - 1] ?? '',
  };
}

function label(state: BillingState): MessageKey {
  return `ledger.state.${state}` as MessageKey;
}

/** Everything the numbers touch: the card's figures move with the ledger. */
export function invalidate(queryClient: ReturnType<typeof useQueryClient>): void {
  for (const key of [['ledger'], ['projects'], ['unbilled'], ['reports'], ['time-entries']]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

function totalOf(amounts: { currency: string; amount: string }[] | undefined): string | null {
  if (!amounts || amounts.length === 0) return null;
  return amounts
    .map((one) => formatAmount(Number(one.amount), one.currency))
    .join(' + ');
}

/**
 * The plain number to pre-fill a payment with: what the picked items are
 * worth, less what is already in. Empty when the selection spans currencies,
 * because one payment cannot be in two of them.
 */
function dueTotal(items: LedgerItem[]): string {
  if (currenciesOf(items).size !== 1) return '';
  const owed = items
    .filter((item) => item.due && item.state !== 'WRITTEN_OFF')
    .reduce((total, item) => total + Number(item.amount) - paidOn(item), 0);
  return owed > 0 ? owed.toFixed(2) : '';
}

function currenciesOf(items: LedgerItem[]): Set<string> {
  return new Set(items.filter((item) => item.due).map((item) => item.currency));
}

/**
 * How much of one item is already in. A paid item carries no figure because
 * PAID means all of it — reading the empty field as "nothing paid" is what had
 * the payment box offering to collect 3 300 when only 2 100 was still owed.
 */
function paidOn(item: LedgerItem): number {
  if (item.state === 'PAID') return Number(item.amount);
  return Number(item.paid_amount ?? 0);
}

function sumOf(items: LedgerItem[], fallback?: string): string {
  const byCurrency = new Map<string, number>();
  for (const item of items) {
    if (!item.due) continue;
    byCurrency.set(item.currency, (byCurrency.get(item.currency) ?? 0) + Number(item.amount));
  }
  if (byCurrency.size === 0) return formatAmount(0, fallback ?? 'CZK');
  return [...byCurrency.entries()]
    .map(([currency, amount]) => formatAmount(amount, currency))
    .join(' + ');
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day}. ${month}. ${year}`;
}
